import {
  BarChart3,
  Bot,
  History,
  MoreHorizontal,
  Pencil,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneOff,
  Settings2,
  Trash2,
  Workflow,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { AIAgent } from '@/api/aiAgents'

/** Ce qui manque à un agent pour être prêt : sert à l'état et au bouton. */
export type EtatAgent = 'pret' | 'sans_numero' | 'a_terminer' | 'inactif'

export function etatAgent(agent: AIAgent): EtatAgent {
  if (!agent.is_active) return 'inactif'
  if (agent.call_mode === 'outbound') {
    const incomplet =
      !agent.google_calendar || (!agent.can_cancel_appointments && !agent.can_reschedule_appointments)
    return incomplet ? 'a_terminer' : 'pret'
  }
  if (!agent.phone_number && !agent.uses_company_main_number && !(agent.answers_in_flows?.length)) return 'sans_numero'
  return 'pret'
}

/** Teinte stable par agent : même nom, même couleur, sur toutes les pages. */
const TEINTES = [
  'from-sky-100 to-blue-200 text-blue-700 dark:from-sky-900/60 dark:to-blue-800/60 dark:text-sky-200',
  'from-violet-100 to-purple-200 text-violet-700 dark:from-violet-900/60 dark:to-purple-800/60 dark:text-violet-200',
  'from-amber-100 to-orange-200 text-amber-700 dark:from-amber-900/60 dark:to-orange-800/60 dark:text-amber-200',
  'from-emerald-100 to-teal-200 text-emerald-700 dark:from-emerald-900/60 dark:to-teal-800/60 dark:text-emerald-200',
  'from-rose-100 to-pink-200 text-rose-700 dark:from-rose-900/60 dark:to-pink-800/60 dark:text-rose-200',
  'from-cyan-100 to-sky-200 text-cyan-700 dark:from-cyan-900/60 dark:to-sky-800/60 dark:text-cyan-200',
]
function teinte(nom: string) {
  let h = 0
  for (const c of nom) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return TEINTES[h % TEINTES.length]
}

export function AvatarAgent({ agent, taille = 'md' }: { agent: Pick<AIAgent, 'name' | 'call_mode'>; taille?: 'md' | 'lg' }) {
  const sortant = agent.call_mode === 'outbound'
  const Dir = sortant ? PhoneOutgoing : PhoneIncoming
  return (
    <div className={cn('relative shrink-0', taille === 'lg' ? 'h-14 w-14' : 'h-10 w-10')}>
      <div className={cn('h-full w-full rounded-xl bg-gradient-to-br flex items-center justify-center ring-1 ring-black/5 dark:ring-white/10', teinte(agent.name))}>
        <Bot className={taille === 'lg' ? 'h-7 w-7' : 'h-5 w-5'} strokeWidth={1.75} />
      </div>
      <span
        className={cn(
          'absolute -bottom-1 -right-1 h-4.5 w-4.5 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-900',
          sortant ? 'bg-emerald-600' : 'bg-blue-600',
        )}
        style={{ height: 18, width: 18 }}
        aria-hidden="true"
      >
        <Dir className="h-2.5 w-2.5 text-white" strokeWidth={2.5} />
      </span>
    </div>
  )
}

const LANGUES: Record<string, string> = { fr: 'FR', en: 'EN', multi: 'FR · EN', 'fr-en': 'FR · EN' }

function dateCourte(iso: string | null | undefined, locale: string) {
  if (!iso) return null
  const d = new Date(iso)
  const jours = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (jours === 0) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
}

interface Props {
  agent: AIAgent
  onEdit: () => void
  onTest: () => void
  onAnswerMode: () => void
  onAnalytics: () => void
  onHistory: () => void
  onDelete: () => void
}

export function AgentCard({ agent, onEdit, onTest, onAnswerMode, onAnalytics, onHistory, onDelete }: Props) {
  const { t, i18n } = useTranslation()
  const etat = etatAgent(agent)
  const sortant = agent.call_mode === 'outbound'
  const act = agent.activity
  const dernier = dateCourte(act?.last_call_at, i18n.language)

  const ETATS: Record<EtatAgent, { label: string; classe: string; point: string }> = {
    pret: {
      label: t('manager.aiAgents.etat.pret', 'Prêt'),
      classe: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10',
      point: 'bg-emerald-500',
    },
    sans_numero: {
      label: t('manager.aiAgents.etat.sansNumero', 'Sans numéro'),
      classe: 'text-amber-700 dark:text-amber-300 bg-amber-500/10',
      point: 'bg-amber-500',
    },
    a_terminer: {
      label: t('manager.aiAgents.etat.aTerminer', 'À terminer'),
      classe: 'text-amber-700 dark:text-amber-300 bg-amber-500/10',
      point: 'bg-amber-500',
    },
    inactif: {
      label: t('manager.aiAgents.etat.inactif', 'En pause'),
      classe: 'text-slate-600 dark:text-slate-300 bg-slate-500/10',
      point: 'bg-slate-400',
    },
  }
  const e = ETATS[etat]

  return (
    <div
      className={cn(
        'group grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto] items-center gap-4 rounded-xl border px-4 py-3.5 transition-colors',
        'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700',
      )}
    >
      {/* Qui */}
      <div className="flex items-center gap-3 min-w-0">
        <AvatarAgent agent={agent} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <button type="button" onClick={onEdit} className="text-[15px] font-semibold text-slate-900 dark:text-white truncate hover:underline">
              {agent.name}
            </button>
            <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium', e.classe)}>
              <span className={cn('h-1.5 w-1.5 rounded-full', e.point)} />
              {e.label}
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {LANGUES[agent.language] ?? agent.language.toUpperCase()}
            {agent.voice_id ? '' : ` · ${t('manager.aiAgents.etat.sansVoix', 'Sans voix')}`}
          </p>
        </div>
      </div>

      {/* La ligne */}
      <div className="min-w-0 text-sm">
        {sortant ? (
          <span className="text-slate-500 dark:text-slate-400">
            {t('manager.aiAgents.ligne.campagne', 'Numéro choisi à chaque campagne')}
          </span>
        ) : etat === 'sans_numero' ? (
          <button
            type="button"
            onClick={onAnswerMode}
            className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300 hover:underline"
          >
            <PhoneOff className="h-3.5 w-3.5" />
            {t('manager.aiAgents.ligne.aucune', 'Ne répond sur aucune ligne')}
            <span className="font-semibold">· {t('manager.aiAgents.ligne.brancher', 'Brancher')}</span>
          </button>
        ) : !agent.phone_number && !agent.uses_company_main_number ? (
          <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
            <Workflow className="h-3.5 w-3.5 text-slate-400" />
            {t('manager.aiAgents.ligne.flux', 'Dans le flux « {{nom}} »', { nom: agent.answers_in_flows?.[0] })}
            {(agent.answers_in_flows?.length ?? 0) > 1 && (
              <span className="text-slate-400">+{(agent.answers_in_flows?.length ?? 1) - 1}</span>
            )}
          </span>
        ) : (
          <button type="button" onClick={onAnswerMode} className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200 hover:underline">
            <Phone className="h-3.5 w-3.5 text-slate-400" />
            <span className="num-tabular">
              {agent.uses_company_main_number && !agent.phone_number
                ? t('manager.aiAgents.ligne.principal', 'Numéro principal')
                : agent.phone_number_display}
            </span>
          </button>
        )}
      </div>

      {/* Activité 30 jours */}
      <div className="text-sm">
        {act && act.calls_30d > 0 ? (
          <div className="flex items-baseline gap-3">
            <span className="font-semibold text-slate-900 dark:text-white num-tabular">
              {t('manager.aiAgents.activite.appels', '{{count}} appels', { count: act.calls_30d })}
            </span>
            <span className="text-slate-500 dark:text-slate-400 num-tabular">
              {t('manager.aiAgents.activite.minutes', '{{count}} min', { count: act.minutes_30d })}
            </span>
            {dernier && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {t('manager.aiAgents.activite.dernier', 'dernier {{quand}}', { quand: dernier })}
              </span>
            )}
          </div>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">
            {t('manager.aiAgents.activite.aucun', 'Aucun appel ce mois-ci')}
          </span>
        )}
      </div>

      {/* Actions : icônes toujours visibles, sans mots */}
      <div className="flex items-center gap-0.5 justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onTest}
          disabled={!agent.is_active}
          title={agent.is_active ? t('manager.aiAgents.actions.tester', 'Tester') : t('manager.aiAgents.testCall.inactiveHint')}
          aria-label={t('manager.aiAgents.actions.tester', 'Tester')}
          className="h-8 w-8 text-slate-500 hover:text-slate-900 dark:hover:text-white"
        >
          <PhoneCall className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEdit}
          title={t('manager.aiAgents.edit')}
          aria-label={t('manager.aiAgents.edit')}
          className="h-8 w-8 text-slate-500 hover:text-slate-900 dark:hover:text-white"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              {t('manager.aiAgents.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onTest} disabled={!agent.is_active}>
              <PhoneCall className="h-4 w-4 mr-2" />
              {t('manager.aiAgents.testCall.menuLabel')}
            </DropdownMenuItem>
            {!sortant && (
              <DropdownMenuItem onClick={onAnswerMode}>
                <Settings2 className="h-4 w-4 mr-2" />
                {t('manager.aiAgents.answerMode.menuLabel')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onAnalytics}>
              <BarChart3 className="h-4 w-4 mr-2" />
              {t('manager.aiAgents.viewAnalytics')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onHistory}>
              <History className="h-4 w-4 mr-2" />
              {t('manager.aiAgents.callHistoryMenu')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600">
              <Trash2 className="h-4 w-4 mr-2" />
              {t('manager.aiAgents.deleteMenu')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
